import { Grid, List, ListItem, TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { useEffect, useState } from "react";
import { Hardware, Software } from "../../api/api-generated";
import {
  useFindAllHardwareQuery,
  useFindAllLicensesByCompanyIdQuery,
  useFindAllSoftwareLinksQuery,
  useFindAllSystemModuleLicensesByCompanyIdQuery,
  useFindOneCompanyQuery,
} from "../../api/api-hooks";
import LinkNewTab from "../../components/LinkNewTab";
import useStyles from "./softwareDisplayStyles";

interface Props {
  softwareType: Software;
  companyId: number;
}

const AdminDocumentationDisplay = ({ softwareType, companyId }: Props) => {
  const { data: allLinks } = useFindAllSoftwareLinksQuery({});
  const { data: allHardware } = useFindAllHardwareQuery({});
  const { data: licenses } = useFindAllLicensesByCompanyIdQuery({
    id: companyId,
  });
  const { data: systemModules } =
    useFindAllSystemModuleLicensesByCompanyIdQuery({ id: companyId });
  const { data: company } = useFindOneCompanyQuery({ id: companyId });
  const [modules, setModules] = useState<string[]>([]);
  const classes = useStyles();
  const [selectedHardware, setSelectedHardware] = useState<
    Omit<Hardware, "licenses"> | null | undefined
  >(null);

  useEffect(() => {
    if (!licenses) return;
    // Get modules for selected hardware
    const licensesForSelectedHardware = licenses.filter((license) => {
      if (!selectedHardware) return false;
      return license.hardwareId === selectedHardware.id;
    });
    const newModules: string[] = [];
    for (const license of licensesForSelectedHardware) {
      if (license.module) {
        if (!newModules.includes(license.module.moduleName)) {
          newModules.push(license.module.moduleName);
        }
      }
    }
    setModules(newModules);
  }, [selectedHardware, licenses]);

  if (
    allLinks === undefined ||
    allHardware === undefined ||
    licenses === undefined ||
    company === undefined ||
    systemModules === undefined
  )
    return null;
  const filteredLinks = allLinks.filter(
    (link) => link.softwareId === softwareType.id
  );
  const filteredHardware = allHardware.filter((item) => {
    let hasAssociatedSoftware = false;
    for (const license of licenses) {
      if (
        license.softwareId === softwareType.id &&
        license.hardwareId === item.id
      ) {
        hasAssociatedSoftware = true;
      }
    }
    const companyIdsForItem = item.companies.map(
      (hardwareItem) => hardwareItem.id
    );
    return companyIdsForItem.includes(companyId) && hasAssociatedSoftware;
  });

  const handleHardwareChange = (
    event: any,
    autoboxValue: Omit<Hardware, "licenses"> | null
  ) => {
    setSelectedHardware(autoboxValue);
  };
  return (
    <Grid
      item
      container
      direction="column"
      alignItems="flex-start"
      spacing={2}
      className={classes.formContainer}
    >
      <Grid item>
        <Typography variant="h5">Documentation</Typography>
      </Grid>
      {filteredLinks.map((link, idx) => (
        <Grid key={idx} item>
          <LinkNewTab href={link.link} label={link.name} />
        </Grid>
      ))}
      {softwareType.type.toLowerCase().includes("spectras") && (
        <>
          <Grid item>
            <Typography variant="h5">
              Modules ({company.companyName})
            </Typography>
          </Grid>
          <Grid item>
            <Autocomplete
              onChange={handleHardwareChange}
              value={selectedHardware}
              options={filteredHardware}
              getOptionLabel={(hardware) => hardware.serial}
              style={{ width: "300px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Hardware to See Modules"
                  variant="outlined"
                />
              )}
            />
          </Grid>
          <Grid item>
            <Typography variant="h5">System-Level Modules</Typography>
          </Grid>
          <Grid item>
            <List dense={true}>
              {systemModules.length < 1 && <ListItem>None</ListItem>}
              {systemModules.map((item, idx) => (
                <ListItem key={idx}>
                  {item.module
                    .split("_")
                    .map((name) => name[0] + name.slice(1).toLowerCase())
                    .join(" ")}
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item>
            <List dense={true}>
              {selectedHardware && modules.length < 1 && (
                <ListItem>None</ListItem>
              )}
              {modules.map((moduleName, idx) => (
                <ListItem key={idx}>
                  <Typography>{moduleName}</Typography>
                </ListItem>
              ))}
            </List>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default AdminDocumentationDisplay;
